import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp, Play, Volume2, VolumeX, X } from "lucide-react";
import { Reveal, Eyebrow } from "./Decorative";
import { CityBookButtons } from "./Shows";
import { trackReelOpen, trackReelView, trackNotifyClick } from "../lib/track";
import { INTEREST_ANCHOR } from "../lib/constants";
import manifest from "../data/reels.json";

/**
 * Antara reels, self-hosted.
 *
 * Instagram's embed cannot be paused from the page, crops to its own frame
 * and links out to Instagram, so the reels are served from public/reels/
 * (built by scripts/build-reels.mjs) and played in our own <video>.
 *
 * On the page: one 9:16 card, so the section is one reel tall whatever the
 * count. Tapping it opens a full-screen viewer that works like Instagram:
 * reels stacked vertically, swipe up for the next, and only the reel on
 * screen plays — every other one is paused and rewound, so two soundtracks
 * can never overlap. Booking stays pinned at the bottom of the viewer, and
 * the browser's back button closes the viewer instead of leaving the site.
 *
 * With an empty manifest the section renders nothing.
 */

const P = process.env.PUBLIC_URL || "";
const REELS = manifest.reels.map((r) => ({
  ...r,
  mp4: `${P}/reels/${r.id}.mp4`,
  webm: `${P}/reels/${r.id}.webm`,
  poster: `${P}/reels/${r.id}.jpg`,
}));

// H.264 where the browser has it (Safari, iPhone, Chrome), else VP9 WebM.
// Picked once in JS rather than with <source> children, because the viewer
// sets src lazily and a late <source> would not start loading by itself.
let FORMAT = null;
function format() {
  if (!FORMAT) {
    const v = document.createElement("video");
    FORMAT = v.canPlayType('video/mp4; codecs="avc1.4D401F, mp4a.40.2"') ? "mp4" : "webm";
  }
  return FORMAT;
}

export default function ShowReels({ shows }) {
  const [openAt, setOpenAt] = useState(null);
  const close = useCallback(() => setOpenAt(null), []);
  if (!REELS.length) return null;

  const open = (i) => {
    trackReelOpen(i);
    setOpenAt(i);
  };
  const first = REELS[0];

  return (
    <section className="bg-[#0F0A07] pb-20 md:pb-28 px-6" data-testid="show-reels">
      <div className="max-w-6xl mx-auto">
        <Reveal>
          <div className="text-center">
            <Eyebrow>From the room</Eyebrow>
            <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight" style={{ color: "#FBF6EE" }}>
              You came to listen. Watch the room sing.
            </h2>
          </div>
        </Reveal>

        <button
          type="button"
          onClick={() => open(0)}
          className="reel-card group mt-8 md:mt-10"
          aria-label={`Watch ${REELS.length} reels from Antara evenings`}
          data-testid="reel-card"
        >
          {REELS.length > 1 && <span className="reel-card-stack" aria-hidden="true" />}
          <span className="reel-card-frame">
            <img
              src={first.poster}
              alt=""
              width={first.width}
              height={first.height}
              loading="lazy"
              decoding="async"
              className="reel-card-img"
            />
            <span className="reel-play" aria-hidden="true"><Play size={24} strokeWidth={2} fill="currentColor" /></span>
            <span className="reel-card-label">
              {REELS.length > 1 ? `Watch ${REELS.length} reels · swipe up for the next` : "Watch the reel"}
            </span>
          </span>
        </button>

        <div className="mt-10">
          {shows.length > 0 ? (
            <CityBookButtons shows={shows} placement="reels" className="mx-auto max-w-[480px] md:max-w-3xl" />
          ) : (
            <div className="text-center">
              <a href={`#${INTEREST_ANCHOR}`} onClick={trackNotifyClick} className="cta-primary" data-testid="reels-interest-btn">
                Bring Antara to your city <span className="cta-arrow">→</span>
              </a>
            </div>
          )}
        </div>
      </div>

      {openAt !== null && <ReelViewer start={openAt} shows={shows} onClose={close} />}
    </section>
  );
}

function ReelViewer({ start, shows, onClose }) {
  const scroller = useRef(null);
  const videos = useRef([]);
  const [active, setActive] = useState(start);
  const [muted, setMuted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [swiped, setSwiped] = useState(false);

  // The viewer is a history entry: back closes it rather than leaving the
  // site. The close button goes through history too, so the two agree.
  useEffect(() => {
    window.history.pushState({ reelViewer: true }, "");
    window.addEventListener("popstate", onClose);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("popstate", onClose);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);
  const close = () => window.history.back();

  useLayoutEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = start * el.clientHeight;
  }, [start]);

  // The reel on screen is the active one. Every reel is exactly one
  // scroller tall, so it is the scroll position divided by that height —
  // the same arithmetic in every browser, where an IntersectionObserver on a
  // snapping scroller is not.
  // Worked out on every scroll event, and also polled while the viewer is
  // open: Safari (WebKit) has been seen to land a scroll without delivering
  // the scroll event, which left the old reel playing. setActive with an
  // unchanged index costs nothing, so the poll is a cheap guarantee.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return undefined;
    const sync = () => {
      const i = Math.round(el.scrollTop / el.clientHeight);
      setActive(Math.max(0, Math.min(REELS.length - 1, i)));
    };
    el.addEventListener("scroll", sync, { passive: true });
    el.addEventListener("scrollend", sync, { passive: true });
    const poll = setInterval(sync, 200);
    return () => {
      el.removeEventListener("scroll", sync);
      el.removeEventListener("scrollend", sync);
      clearInterval(poll);
    };
  }, []);

  // Exactly one reel plays. If the browser refuses sound without a fresh
  // tap (iOS can), fall back to muted playback and show the sound button.
  useEffect(() => {
    setPaused(false);
    videos.current.forEach((v, i) => {
      if (!v) return;
      if (i === active) {
        v.muted = muted;
        const p = v.play();
        if (p && p.catch) {
          p.catch((err) => {
            if (err && err.name === "NotAllowedError" && !v.muted) setMuted(true);
            else setPaused(true);
          });
        }
      } else {
        v.pause();
        if (v.currentTime) v.currentTime = 0;
      }
    });
  }, [active, muted]);

  useEffect(() => {
    trackReelView(active);
    if (active !== start) setSwiped(true);
  }, [active, start]);

  // Leaving the tab (BookMyShow opens in a new one) pauses the reel.
  useEffect(() => {
    const onHide = () => {
      const v = videos.current[active];
      if (document.hidden && v) { v.pause(); setPaused(true); }
    };
    document.addEventListener("visibilitychange", onHide);
    return () => document.removeEventListener("visibilitychange", onHide);
  }, [active]);

  // To the reel after (or before) the one nearest the top, as an absolute
  // position. A relative scrollBy issued mid-animation could not pass the
  // reel it was still settling on (scroll-snap-stop: always), so a quick
  // second press went nowhere.
  const step = (dir) => {
    const el = scroller.current;
    if (!el) return;
    const h = el.clientHeight;
    const target = Math.max(0, Math.min(REELS.length - 1, Math.round(el.scrollTop / h) + dir));
    el.scrollTo({ top: target * h, behavior: "smooth" });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") window.history.back();
      else if (e.key === "ArrowDown") { e.preventDefault(); step(1); }
      else if (e.key === "ArrowUp") { e.preventDefault(); step(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const togglePlay = () => {
    const v = videos.current[active];
    if (!v) return;
    if (v.paused) { v.play().catch(() => {}); setPaused(false); } else { v.pause(); setPaused(true); }
  };

  return createPortal(
    <div className="reel-viewer" role="dialog" aria-modal="true" aria-label="Antara reels" data-testid="reel-viewer">
      <div ref={scroller} className="reel-viewer-scroll" data-testid="reel-viewer-scroll">
        {REELS.map((r, i) => (
          <div key={r.id} className="reel-viewer-item">
            <video
              ref={(el) => { videos.current[i] = el; }}
              className="reel-viewer-video"
              src={Math.abs(i - active) <= 1 ? r[format()] : undefined}
              poster={r.poster}
              preload={i === active ? "auto" : "metadata"}
              playsInline
              loop
              onClick={togglePlay}
              data-testid={`reel-video-${i}`}
            />
            {paused && i === active && (
              <span className="reel-play pointer-events-none" aria-hidden="true">
                <Play size={26} strokeWidth={2} fill="currentColor" />
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="reel-viewer-top">
        <span className="font-body text-sm tracking-wide" data-testid="reel-counter">{active + 1} / {REELS.length}</span>
        <div className="flex items-center gap-2">
          <button type="button" className="reel-viewer-btn" onClick={() => setMuted((m) => !m)} aria-label={muted ? "Turn sound on" : "Mute"} data-testid="reel-mute">
            {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
          </button>
          <button type="button" className="reel-viewer-btn" onClick={close} aria-label="Close reels" data-testid="reel-close">
            <X size={20} />
          </button>
        </div>
      </div>

      {REELS.length > 1 && (
        <div className="reel-viewer-arrows">
          <button type="button" className="reel-viewer-btn" onClick={() => step(-1)} disabled={active === 0} aria-label="Previous reel"><ChevronUp size={20} /></button>
          <button type="button" className="reel-viewer-btn" onClick={() => step(1)} disabled={active === REELS.length - 1} aria-label="Next reel"><ChevronDown size={20} /></button>
        </div>
      )}

      <div className="reel-viewer-bottom">
        {!swiped && REELS.length > 1 && active < REELS.length - 1 && (
          <p className="mb-3 flex items-center justify-center gap-1.5 font-body text-xs tracking-wide text-[#F5EFE6]/85" data-testid="reel-swipe-hint">
            <ChevronUp size={14} className="animate-bounce" /> Swipe up for the next reel
          </p>
        )}
        {shows.length > 0 ? (
          <CityBookButtons shows={shows} placement="reel-viewer" className="mx-auto max-w-[480px]" />
        ) : (
          // Nothing to book: close the viewer (through history, like the X),
          // then take the visitor to the city form.
          <button
            type="button"
            onClick={() => {
              trackNotifyClick();
              window.history.back();
              setTimeout(() => {
                const el = document.getElementById(INTEREST_ANCHOR);
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }, 80);
            }}
            className="cta-primary mx-auto flex w-full max-w-[480px] justify-center"
            style={{ padding: "14px 24px", fontSize: 15 }}
            data-testid="reel-viewer-interest-btn"
          >
            Bring Antara to your city <span className="cta-arrow">→</span>
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
